import { USER_LOGGED_IN, TAB_DIRTY_SESSION } from '../constants';
import { DataObject, fetchTimeoutRedirectUrl, addLocalePrefix, clearLoggedInSessionData } from '../utils';

export interface SessionTimeoutConfig {
  submitActionLogin?: string;
  submitActionAnonymous?: string;
  isLoggedIn?: boolean;
  onTimeout?: () => void;
  onUserActivity?: () => void;
  timeoutMs?: number;
}

export class SessionTimeout {
  expired: number;
  timer: number;
  submitActionLogin?: string;
  submitActionAnonymous?: string;
  isLoggedIn: boolean;
  onTimeout?: () => void;
  onUserActivity?: () => void;
  private isInitialized = false;

  static TRUE_VAL = 'true';
  static FALSE_VAL = 'false';

  constructor(config: SessionTimeoutConfig) {
    // Use timeoutMs from config if provided, otherwise fall back to environment variable
    let timeout = 0;

    if (config.timeoutMs !== undefined) {
      // Use the timeout passed from config (already in milliseconds)
      timeout = config.timeoutMs;
    } else if (typeof process !== 'undefined' && process.env?.['NEXT_PUBLIC_SESSION_TIMEOUT_MS']) {
      // Fall back to environment variable (in seconds) and convert to milliseconds
      timeout = parseInt(process.env['NEXT_PUBLIC_SESSION_TIMEOUT_MS'], 10) * 1000;
    }

    this.expired = timeout;
    this.timer = 0;
    this.isLoggedIn = config.isLoggedIn || false;
    this.submitActionLogin = config.submitActionLogin;
    this.submitActionAnonymous = config.submitActionAnonymous;
    this.onTimeout = config.onTimeout;
    this.onUserActivity = config.onUserActivity;
  }

  /**
   * initializing session
   * isLoggedIn flag to handle the action URL
   */
  setupForAuth = (): void => {
    this.isLoggedIn = true;
    this.initialize();
  };

  /**
   * Initialize session timeout - call this from your _app.tsx useEffect
   */
  initialize = (): void => {
    if (this.isInitialized || typeof window === 'undefined') return;

    this.isInitialized = true;
    this.setStorageSession(SessionTimeout.TRUE_VAL);

    // reset session on user actions
    ['click', 'touchstart', 'keydown', 'contextmenu'].forEach(evt =>
      document.addEventListener(evt, this.startTimer, { passive: true })
    );

    // to handle change in localstorage value from another window/tab
    window.addEventListener('storage', this.handleStorageEvent);

    // listen for user login events
    document.addEventListener(USER_LOGGED_IN, this.setupForAuth);

    this.startTimer();
  };

  /**
   * Set session dirtiness in localstorage
   * @param value
   */
  setStorageSession(value: string): void {
    window.localStorage?.setItem(TAB_DIRTY_SESSION, value);
  }

  /**
   * start timeout for given expired value
   */
  startTimer = (): void => {
    if (this.expired > 0) {
      clearTimeout(this.timer);
      this.timer = window.setTimeout(() => {
        this.timeoutRedirect();
      }, this.expired);
      this.setStorageSession(SessionTimeout.FALSE_VAL);

      // Call custom user activity callback if provided
      this.onUserActivity?.();
    }
  };

  /**
   * Restart timer if session gets dirty from another tab
   * @param storageEvent
   */
  handleStorageEvent = (storageEvent: StorageEvent): void => {
    if (storageEvent.key === TAB_DIRTY_SESSION && storageEvent.newValue === SessionTimeout.TRUE_VAL) {
      this.startTimer();
    }
  };

  /**
   * Handle session timeout - redirect or call custom callback
   */
  timeoutRedirect = async (): Promise<void> => {
    // Call custom timeout callback if provided
    if (this.onTimeout) {
      this.onTimeout();
      return;
    }

    // Default behavior: redirect using API
    const submitAction = this.isLoggedIn ? this.submitActionLogin : this.submitActionAnonymous;
    if (submitAction) {
      try {
        const data = await fetchTimeoutRedirectUrl(submitAction);
        const { redirect, nextStateURI } = data as DataObject;
        if (redirect === true) {
          clearLoggedInSessionData();
          window.location.href = addLocalePrefix(window?.location?.pathname || '', nextStateURI);
        }
      } catch (error) {
        console.error('Session timeout redirect failed:', error);
        // Fallback: clear session data and redirect to home
        clearLoggedInSessionData();
        window.location.href = '/';
      }
    }
  };
  /**
   * Update login status - call this when user logs in/out
   */
  updateLoginStatus = (isLoggedIn: boolean): void => {
    this.isLoggedIn = isLoggedIn;
  };

  /**
   * Update timeout duration - call this to change session timeout
   */
  updateTimeout = (expiredInMs: number): void => {
    this.expired = expiredInMs;
    // Restart timer with new duration
    if (this.isInitialized) {
      this.startTimer();
    }
  };

  /**
   * Cleanup - call this when component unmounts
   */
  cleanup = (): void => {
    if (!this.isInitialized) return;

    clearTimeout(this.timer);

    // Remove event listeners
    ['click', 'touchstart', 'keydown', 'contextmenu'].forEach(evt =>
      document.removeEventListener(evt, this.startTimer)
    );

    window.removeEventListener('storage', this.handleStorageEvent);
    document.removeEventListener(USER_LOGGED_IN, this.setupForAuth);

    this.isInitialized = false;
  };
}
